import React from 'react';
import { Dropdown } from 'antd';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg?react';

export interface PlanSortOption {
  key: string;
  label: string;
  field: string;
  dir: 'asc' | 'desc';
}

export const PLAN_SORT_OPTIONS: PlanSortOption[] = [
  { key: 'name-asc', label: 'Plan Name (A→Z)', field: 'name', dir: 'asc' },
  { key: 'name-desc', label: 'Plan Name (Z→A)', field: 'name', dir: 'desc' },
  {
    key: 'created-desc',
    label: 'Creation Date (New→Old)',
    field: 'createdAt',
    dir: 'desc'
  },
  {
    key: 'created-asc',
    label: 'Creation Date (Old→New)',
    field: 'createdAt',
    dir: 'asc'
  }
];

export interface PlanSortDropdownProps {
  sortBy: string;
  sortDir: string;
  onSortChange: (field: string, dir: 'asc' | 'desc') => void;
}

const PLAN_SORT_STORAGE_KEY = 'plans-sort-params';

type PlanSort = { sortBy: string; sortDir: 'asc' | 'desc' };

const DEFAULT_PLAN_SORT: PlanSort = { sortBy: 'createdAt', sortDir: 'desc' };

/**
 * Reads a previously-chosen sort from `sessionStorage`, matching
 * Applications/Work Orders' `useOperationSort` persistence. Kept local to
 * Plans since `usePlans()` only tracks one `sortBy`/`sortDir` pair (unlike
 * that hook's multi-field `SortConfig[]`), so other `usePlans()` callers stay unaffected.
 */
export const readPersistedPlanSort = (
  fallback: PlanSort = DEFAULT_PLAN_SORT
): PlanSort => {
  if (typeof sessionStorage === 'undefined') return fallback;

  try {
    const stored = sessionStorage.getItem(PLAN_SORT_STORAGE_KEY);
    if (!stored) return fallback;

    const parsed = JSON.parse(stored);
    const isValid = PLAN_SORT_OPTIONS.some(
      option => option.field === parsed?.sortBy && option.dir === parsed?.dir
    );

    return isValid ? { sortBy: parsed.sortBy, sortDir: parsed.dir } : fallback;
  } catch {
    // Malformed JSON or a sessionStorage access error (disabled storage,
    // cross-origin restrictions) — falling back to the default sort is the
    // only sensible recovery, so the error itself isn't worth surfacing.
    return fallback;
  }
};

/** Persists the user's chosen sort so it survives a remount (e.g. Create Plan → back to the list). */
export const persistPlanSort = (field: string, dir: 'asc' | 'desc'): void => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(
      PLAN_SORT_STORAGE_KEY,
      JSON.stringify({ sortBy: field, dir })
    );
  } catch {
    // Best-effort — a storage failure (quota exceeded, disabled storage)
    // must not block the sort itself from applying.
  }
};

/**
 * A single sort-option row inside the dropdown overlay, mirroring
 * `SortDropdownMenu`'s own `DropdownMenuItem` (`bg-info-light-blue
 * font-semibold` on the selected option) — that component can't be reused
 * directly since it's coupled to `OperationSortProvider`, but its visual
 * contract is matched exactly here.
 *
 * FR-034: forces black selected-option text over AntD's default blue
 * via an explicit class on this item, scoped so it doesn't leak into
 * other dropdowns reusing header-action-buttons-dropdown.
 */
const SortMenuItem: React.FC<{ label: string; isSelected: boolean }> = ({
  label,
  isSelected
}) => (
  <div
    className={`w-full px-3 py-1 text-sm text-left ${
      isSelected ? 'bg-info-light-blue font-semibold text-neutral-1000' : ''
    }`}
  >
    {label}
  </div>
);

/**
 * Sort control for the Plans list. Not `SortDropdownMenu`
 * (`components/RecordsListView/SortDropdownMenu`) because that component is
 * tightly coupled to `OperationSortProvider`/`useOperationSortContext`, which
 * Plans doesn't adopt (`usePlans()`'s own `sortBy`/`sortDir`/`sort()` are used
 * as-is instead, per the "match legacy behavior" data-layer decision) — but
 * the trigger button mirrors `SortDropdownMenu`'s exact contract ("Sort: "
 * prefix, fixed 166px width, sort icon, the `(A→Z)`-style suffix stripped)
 * since `ListView`'s nav panel is a fixed 330px column: the full option
 * label (e.g. "Plan Name (A→Z)") overflows that column's available width,
 * the same reason `SortDropdownMenu` only shows the full label inside its
 * own menu items, never on the trigger itself.
 */
const PlanSortDropdown: React.FC<PlanSortDropdownProps> = ({
  sortBy,
  sortDir,
  onSortChange
}) => {
  const selected = PLAN_SORT_OPTIONS.find(
    option => option.field === sortBy && option.dir === sortDir
  );
  const selectedLabel = selected ? selected.label.split(' (')[0] : 'Plan Name';

  const menuItems = PLAN_SORT_OPTIONS.map(option => ({
    key: option.key,
    label: (
      <SortMenuItem label={option.label} isSelected={option === selected} />
    ),
    onClick: () => onSortChange(option.field, option.dir)
  }));

  return (
    <Dropdown
      menu={{ items: menuItems, selectedKeys: selected ? [selected.key] : [] }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="header-action-buttons-dropdown"
    >
      <Button
        type={ButtonType.outline}
        data-testid="plan-sort-dropdown"
        size="middle"
        className="flex items-center"
        style={{ width: '166px', padding: '0 10px', gap: '4px' }}
      >
        <SortIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
        <span
          style={{
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            letterSpacing: '-0.105px'
          }}
        >
          <span>Sort:</span>
          <span style={{ marginLeft: '3px' }}>{selectedLabel}</span>
        </span>
      </Button>
    </Dropdown>
  );
};

export default PlanSortDropdown;
